'use client';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { sanitizeImageUrl, fallbackImage } from '@/lib/image-safety';

const FALLBACK_SLIDE = '/images/sliders/1.webp';

export const ImagesSlider = ({
  images,
  children,
  overlay = true,
  overlayClassName,
  className,
  autoplay = true,
  direction = 'up',
  transitionStyle = 'fade',
}: {
  images: string[];
  children: React.ReactNode;
  overlay?: React.ReactNode;
  overlayClassName?: string;
  className?: string;
  autoplay?: boolean;
  direction?: 'up' | 'down';
  transitionStyle?: 'scale-rotate' | 'fade' | 'slide'; // <-- SUPPORTED STYLES
}) => {
  const sanitizedImages = useMemo(() => {
    const unique = Array.from(
      new Set(
        images
          .map((src) => sanitizeImageUrl(src))
          .filter((src) => typeof src === 'string' && src.trim().length > 0)
      )
    );
    return unique.length > 0 ? unique : [FALLBACK_SLIDE];
  }, [images]);
  const totalSlides = sanitizedImages.length;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [failedMap, setFailedMap] = useState<Record<string, boolean>>({});
  const [isDocumentVisible, setIsDocumentVisible] = useState(true);
  const autoplayInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setCurrentIndex(0);
  }, [sanitizedImages]);

  useEffect(() => {
    if (currentIndex >= totalSlides) {
      setCurrentIndex(0);
    }
  }, [currentIndex, totalSlides]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1 === totalSlides ? 0 : prevIndex + 1));
  }, [totalSlides]);

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 < 0 ? totalSlides - 1 : prevIndex - 1));
  }, [totalSlides]);

  const markFailed = useCallback((src: string) => {
    setFailedMap((prev) => {
      if (prev[src]) return prev;
      return { ...prev, [src]: true };
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        handleNext();
      } else if (event.key === 'ArrowLeft') {
        handlePrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleNext, handlePrevious]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handler = () => setIsDocumentVisible(!document.hidden);
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  useEffect(() => {
    if (autoplayInterval.current) {
      clearInterval(autoplayInterval.current);
      autoplayInterval.current = null;
    }
    const shouldAutoplay = autoplay && isDocumentVisible && totalSlides > 1;
    if (!shouldAutoplay) {
      return undefined;
    }
    autoplayInterval.current = setInterval(() => {
      handleNext();
    }, 5000);

    return () => {
      if (autoplayInterval.current) {
        clearInterval(autoplayInterval.current);
        autoplayInterval.current = null;
      }
    };
  }, [autoplay, handleNext, isDocumentVisible, totalSlides]);

  useEffect(() => {
    if (typeof window === 'undefined' || totalSlides <= 1) return;
    const nextIndex = (currentIndex + 1) % totalSlides;
    const Preloader = window.Image;
    const preloader = new Preloader();
    preloader.decoding = 'async';
    preloader.src = sanitizedImages[nextIndex];
    return () => {
      preloader.onload = null;
      preloader.onerror = null;
    };
  }, [currentIndex, sanitizedImages, totalSlides]);

  // Animation Variants
  const animationVariants = useMemo(
    () => ({
      'scale-rotate': {
        initial: { scale: 0, opacity: 0, rotateX: 45 },
        animate: {
          scale: 1,
          opacity: 1,
          rotateX: 0,
          transition: { duration: 0.5 },
        },
        exit: {
          opacity: 1,
          y: direction === 'up' ? '-150%' : '150%',
          transition: { duration: 0.8 },
        },
      },
      fade: {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { duration: 0.8 } },
        exit: { opacity: 0, transition: { duration: 0.8 } },
      },
      slide: {
        initial: {
          x: direction === 'up' ? '100%' : '-100%',
          opacity: 0,
        },
        animate: {
          x: 0,
          opacity: 1,
          transition: { duration: 0.8 },
        },
        exit: {
          x: direction === 'up' ? '-100%' : '100%',
          opacity: 0,
          transition: { duration: 0.8 },
        },
      },
    }),
    [direction]
  );

  const variants = animationVariants[transitionStyle];
  const originalSlideSrc = sanitizedImages[currentIndex] ?? FALLBACK_SLIDE;
  const currentSlideSrc = failedMap[originalSlideSrc] ? fallbackImage() : originalSlideSrc;
  const isFirstSlide = currentIndex === 0;

  return (
    <div
      className={cn(
        'overflow-hidden h-full w-full relative flex items-center justify-center',
        className
      )}
      style={{ perspective: '1000px' }}
    >
      {children}
      {overlay && <div className={cn('absolute inset-0 bg-black/20 z-40', overlayClassName)} />}

      <AnimatePresence mode='wait'>
        <motion.div
          key={currentSlideSrc}
          initial='initial'
          animate='animate'
          exit='exit'
          variants={variants}
          className='absolute inset-0 h-full w-full'
        >
          <Image
            src={currentSlideSrc}
            alt='Uncommon 12 highlight'
            fill
            sizes='100vw'
            className='h-full w-full object-cover object-center'
            priority={isFirstSlide}
            loading={isFirstSlide ? 'eager' : 'lazy'}
            fetchPriority={isFirstSlide ? 'high' : 'low'}
            decoding='async'
            onError={() => markFailed(originalSlideSrc)}
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
